import React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Link from '@mui/joy/Link';
import Button from '@mui/joy/Button';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Stack from "@mui/joy/Stack";
import Checkbox from '@mui/joy/Checkbox';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import RefreshIcon from '@mui/icons-material/Refresh';
import { NotificationService } from "../../../common/notification";
import Chip from '@mui/material/Chip';
import CircularProgress from "@mui/joy/CircularProgress";
import { botsListOrdersApi, getBotApi } from "../botsApi";
import { BotMode, BotOrder } from '../../../models/bot';
import { ForceActionType, GuaranteedEntity, OrderStatus, OrderStatusDescription, OrderType } from '../../../models/order';
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import { formatDateTime } from "../../../common/dateTime";
import { formatRubles } from "../../../common/money";
import { OrderActions } from '../../orders/OrderActions';
import DoneIcon from '@mui/icons-material/Done';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export function BotOrders() {
    const {id} = useParams();
    const botId = Number(id);
    const navigate = useNavigate();
    const [allOrders, setAllOrders] = React.useState<BotOrder[]>([]);
    const [orders, setOrders] = React.useState<BotOrder[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedOrders, setSelectedOrders] = React.useState<number[]>([]);
    const [orderIdsToForce, setOrderIdsToForce] = React.useState<number[]>([]);
    const [forceActionType, setForceActionType] = React.useState<ForceActionType>(ForceActionType.Complete);
    const [forceModalOpen, setForceModalOpen] = React.useState(false);
    const [manualOnly, setManualOnly] = React.useState(false);
    const [orderType, setOrderType] = React.useState<OrderType | undefined>(undefined);

    React.useEffect(() => {
        loadBot().catch(console.error);
        loadData().catch(console.error);
    }, [])

    // Тип заказов определяется режимом бота
    const loadBot = async () => {
        let result = await getBotApi(botId);
        if (result.result && result.value) {
            setOrderType(result.value.mode === BotMode.P2P ? OrderType.P2P : OrderType.ViaBot);
        } else {
            NotificationService.alertError("Не удалось загрузить данные бота")
        }
    }

    React.useEffect(() => {
        setOrders(allOrders.filter((e) => manualOnly ? e.orderStatus === OrderStatus.ManualMode : true));
        setSelectedOrders([]);
    }, [allOrders, manualOnly])

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await botsListOrdersApi(botId);
            if (result.result) {
                const res = result.value!;
                setAllOrders(res)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const openOrder = (id: number) => {
        navigate(`/app/orders/${id}`);
    }

    const getDefaultActionType = (): ForceActionType => {
        if (orderIdsToForce.length <= 0) {
            return ForceActionType.Complete;
        }

        var toRefund = orderIdsToForce.every((id) => orders.some((order) =>
            order.orderId === id &&
            order.orderStatus === OrderStatus.ManualMode &&
            GuaranteedEntity.isGuaranteed(order)
        ));

        return toRefund ? ForceActionType.Refund : ForceActionType.Complete;
    }

    const forceOrdersAmount = React.useMemo(
        () => orders.filter((e) => orderIdsToForce.some(id => e.orderId === id)).reduce((prev, current) => prev + current.totalAmountIncludingFee, 0),
        [orders, orderIdsToForce],
    );

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                gap: 1,
            }}
        >
            <OrderActions orderIds={orderIdsToForce}
                        orderType={orderType}
                        totalAmount={forceOrdersAmount}
                        actionType={forceActionType}
                        modalOpen={forceModalOpen}
                        setModalOpen={setForceModalOpen}
                        onCompleted={(success: boolean, actionType: ForceActionType, successIds: number[], failedIds: number[]) => {
                            setOrderIdsToForce([]);
                            if (forceActionType === ForceActionType.Handle) {
                                return;
                            }

                            if (success && successIds.length > 0) {
                                loadData();
                            }}} />

            <Box
                sx={{
                    display: 'flex',
                    mb: 1,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                    <Chip label={selectedOrders.length > 0 ? "Снять выбор" : "Выбрать все"}
                        variant="outlined"
                        color={orders.some(e => e.orderStatus === OrderStatus.ManualMode) ? "success" : "default"}
                        disabled={!orders.some(e => e.orderStatus === OrderStatus.ManualMode)}
                        icon={selectedOrders.length > 0 ? <RemoveDoneIcon fontSize='medium' /> : <DoneAllIcon fontSize='medium' />}
                        onClick={selectedOrders.length > 0
                            ? async () => setSelectedOrders([])
                            : async () => setSelectedOrders(orders
                                .filter(e => e.orderStatus === OrderStatus.ManualMode)
                                .map(e => e.orderId))}/>
                    <Chip label="Выбрать гарантийные"
                        variant="outlined"
                        color={orders.some(e => e.orderStatus === OrderStatus.ManualMode && GuaranteedEntity.isGuaranteed(e))
                            ? "success"
                            : "default"}
                        disabled={!orders.some(e => e.orderStatus === OrderStatus.ManualMode && GuaranteedEntity.isGuaranteed(e))}
                        icon={<DoneOutlineIcon fontSize='medium' />}
                        onClick={async () => setSelectedOrders(orders
                                .filter(e => e.orderStatus === OrderStatus.ManualMode && GuaranteedEntity.isGuaranteed(e))
                                .map(e => e.orderId))}/>
                    <Chip label="Возместить выбранные"
                        variant="outlined"
                        color={selectedOrders.length > 0 ? "success" : "default"}
                        disabled={selectedOrders.length === 0}
                        icon={<CurrencyExchangeIcon fontSize='medium' />}
                        onClick={() => {
                            setOrderIdsToForce(selectedOrders);
                            setForceActionType(getDefaultActionType());
                            setForceModalOpen(true);
                        }}/>
                    <Chip label="Обновить"
                        variant="outlined"
                        color="info"
                        icon={<RefreshIcon fontSize='medium' />}
                        onClick={async () => await loadData()}/>
                    
                    <Chip label={manualOnly ? "Показать все" : "Показать только заказы в ручном режиме"}
                        variant="outlined"
                        color="default"
                        sx={{marginLeft: 'auto'}}
                        icon={manualOnly ? <RemoveDoneIcon fontSize='medium' /> : <DoneIcon fontSize='medium' />}
                        onClick={() => setManualOnly(!manualOnly)}/>
            </Box>

            {loading ?
                <CircularProgress/> :
                <>
                    <Sheet
                        className="OrderTableContainer"
                        variant="outlined"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            height: 'auto',
                            maxHeight: '50dvh'
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                                <tr>
                                    <th style={{width: 100, padding: '12px 6px'}}></th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Заказ#</th>
                                    <th style={{width: 240, padding: '12px 6px'}}>Сумма</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Покупатель</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Продавец</th>
                                    <th style={{width: 140, padding: '12px 6px'}}>Гарантия хранения</th>
                                    <th style={{width: 140, padding: '12px 6px'}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((row) => (
                                    <tr key={row.orderId}>
                                        <td>
                                            {row.orderStatus === OrderStatus.ManualMode
                                                ? <Checkbox checked={selectedOrders.some(e => e === row.orderId)}
                                                        onChange={(event) => setSelectedOrders(event.target.checked ?
                                                        [ ...selectedOrders, row.orderId] : selectedOrders.filter(e => e !== row.orderId)
                                                )}/>
                                                : <></>}
                                        </td>
                                        <td>
                                            <Stack spacing={.5} direction={'column'}>
                                                <Link level="body-sm" component="button" onClick={() => openOrder(row.orderId)}>
                                                    #{row.orderId}
                                                </Link>
                                                <Typography level="body-xs" textColor={'background.backdrop'}>{OrderStatusDescription[row.orderStatus]}</Typography>
                                            </Stack>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{formatRubles(row.totalAmountIncludingFee)}</Typography>
                                        </td>
                                        <td>
                                            <Stack spacing={1} direction={'column'}>
                                                <Link
                                                    underline="hover"
                                                    onClick={() => navigate(`/app/users/${row.buyerId.toString()}`)}
                                                    level="body-xs"
                                                >
                                                    {row.buyerId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${row.buyerId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                >
                                                    (Steam link)
                                                </Link>
                                            </Stack>
                                        </td>
                                        <td>
                                            <Stack spacing={1} direction={'column'}>
                                                <Link
                                                    underline="hover"
                                                    onClick={() => navigate(`/app/users/${row.sellerId.toString()}`)}
                                                    level="body-xs"
                                                >
                                                    {row.sellerId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${row.sellerId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                >
                                                    (Steam link)
                                                </Link>
                                            </Stack>
                                        </td>
                                        <td>
                                            {row.receivedItemsAt ?
                                                <Stack spacing={1} direction={{ xs: 'column', sm: 'row'}} alignItems='center'>
                                                    {row.guaranteedUpTo&&
                                                    <>
                                                        {new Date(row.guaranteedUpTo!) < new Date()
                                                            ? <CancelIcon fontSize='medium' color='error' />
                                                            : <CheckCircleIcon fontSize='medium' color='success' />}
                                                    </>
                                                    }

                                                    <Stack spacing={0} direction={'column'}>
                                                        <Typography level="body-xs">{formatDateTime(row.receivedItemsAt!)}</Typography>
                                                        <Typography level="body-xs" marginTop={'-10px'}>..</Typography>
                                                        {row.guaranteedUpTo&&<Typography level="body-xs">{formatDateTime(row.guaranteedUpTo!)}</Typography>}
                                                    </Stack>
                                                </Stack>
                                                : <>-</>
                                            }
                                        </td>
                                        <td>
                                            {row.orderStatus === OrderStatus.ManualMode
                                                ? <Button size="sm" variant="solid" onClick={() => {
                                                    setOrderIdsToForce([row.orderId]);
                                                    setForceActionType(getDefaultActionType());
                                                    setForceModalOpen(true);
                                                }}>
                                                    Возместить
                                                </Button>
                                                : <></>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {orders.map((listItem) => (
                            <List key={listItem.orderId} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <div>
                                            <Typography gutterBottom sx={{fontWeight: 600}}>
                                                Заказ #{listItem.orderId}
                                            </Typography>
                                            <Typography level="body-xs">
                                                Статус: {OrderStatusDescription[listItem.orderStatus!]}
                                            </Typography>
                                            <Typography level="body-xs">Сумма: {formatRubles(listItem.totalAmountIncludingFee)}</Typography>
                                            <Stack spacing={1}
                                                direction={'row'}
                                                sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center"
                                                    }}
                                            >
                                                <Typography level="body-xs">Покупатель:</Typography>
                                                <Link
                                                    underline="hover"
                                                    onClick={() => navigate(`/app/users/${listItem.buyerId.toString()}`)}
                                                    level="body-xs"
                                                    fontSize={14}
                                                >
                                                    {listItem.buyerId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${listItem.buyerId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                    style={{
                                                        minWidth: 80,
                                                        fontSize: 14
                                                    }}
                                                >
                                                    (Steam link)
                                                </Link>
                                            </Stack>
                                            <Stack spacing={1}
                                                direction={'row'}
                                                sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center"
                                                    }}
                                            >
                                                <Typography level="body-xs">Продавец:</Typography>
                                                <Link
                                                    underline="hover"
                                                    onClick={() => navigate(`/app/users/${listItem.sellerId.toString()}`)}
                                                    level="body-xs"
                                                    fontSize={14}
                                                >
                                                    {listItem.sellerId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${listItem.sellerId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                    style={{
                                                        minWidth: 80,
                                                        fontSize: 14
                                                    }}
                                                >
                                                    (Steam link)
                                                </Link>
                                            </Stack>
                                            {listItem.receivedItemsAt ?
                                                <Stack spacing={1}
                                                    direction={'row'}
                                                    sx={{
                                                            justifyContent: "left",
                                                            alignItems: "center"
                                                        }}
                                                >

                                                    {listItem.guaranteedUpTo&&
                                                    <>
                                                        <Typography level="body-xs">Гарантия хранения:</Typography>
                                                        {new Date(listItem.guaranteedUpTo!) < new Date()
                                                            ? <CancelIcon fontSize='medium' color='error' />
                                                            : <CheckCircleIcon fontSize='medium' color='success' />}
                                                    </>
                                                    }
                                                    <Stack spacing={0} direction={'column'}>
                                                        <Typography level="body-xs">{formatDateTime(listItem.receivedItemsAt!)}</Typography>
                                                        <Typography level="body-xs" marginTop={'-10px'}>..</Typography>
                                                        {listItem.guaranteedUpTo&&<Typography level="body-xs">{formatDateTime(listItem.guaranteedUpTo!)}</Typography>}
                                                    </Stack>
                                                </Stack>
                                                :<></>
                                            }
                                        </div>
                                    </ListItemContent>
                                    {listItem.orderStatus === OrderStatus.ManualMode
                                    ? <>
                                        <Checkbox checked={selectedOrders.some(e => e === listItem.orderId)}
                                                onChange={(event) => setSelectedOrders(event.target.checked ?
                                                    [ ...selectedOrders, listItem.orderId] : selectedOrders.filter(e => e !== listItem.orderId))}
                                        />
                                        <Button size="sm" variant="solid" onClick={() => {
                                            setOrderIdsToForce([listItem.orderId]);
                                            setForceActionType(getDefaultActionType());
                                            setForceModalOpen(true);
                                        }}>
                                            Возместить
                                        </Button>
                                    </>
                                    : <></>}
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                    </Box>
                </>
            }
        </Box>
    );
}
